import { useState, useEffect, useRef } from "react";
import { api } from "../../../services/api";
import { useQuery } from "@tanstack/react-query";

interface ProductImageProps {
  codprod: number | string;
  width?: number;
  height?: number;
  alt?: string;
  style?: React.CSSProperties;
}

export function ProductImage({
  codprod,
  width = 40,
  height,
  alt = "imagem do produto",
  style,
}: ProductImageProps) {
  const [src, setSrc] = useState<string | null>(null);
  const [status, setStatus] = useState<"idle" | "loading" | "done" | "error">("idle");
  const containerRef = useRef<HTMLDivElement>(null);

  const imageQuery = useQuery({
    queryKey: ["productImage", codprod],
    enabled: false,
    queryFn: async () => {
      const token = localStorage.getItem("apontamento.jwt");
      const response = await api.get(`/productImage/${codprod}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });

      return response.data as Blob;
    },
  });

  useEffect(() => {
    if (!codprod) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          observer.disconnect();
          imageQuery.refetch();
        }
      },
      { threshold: 0.1 }
    );

    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codprod]);

  useEffect(() => {
    if (imageQuery.isFetching) {
      setStatus("loading");
      return;
    }

    if (imageQuery.isError) {
      setStatus("error");
      return;
    }

    if (imageQuery.data) {
      const objectUrl = URL.createObjectURL(imageQuery.data);
      setSrc((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return objectUrl;
      });
      setStatus("done");
    }
  }, [imageQuery.data, imageQuery.isError, imageQuery.isFetching]);

  // Libera memória quando o componente desmonta
  useEffect(() => {
    return () => {
      if (src) URL.revokeObjectURL(src);
    };
  }, [src]);

  const imgStyle: React.CSSProperties = {
    width: width ?? "auto",
    height: height ?? "auto",
    objectFit: "contain",
    display: "block",
    ...style,
  };

  const placeholderStyle: React.CSSProperties = {
    width: width ?? 40,
    height: height ?? width ?? 40,
    borderRadius: 4,
    background: "linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%)",
    backgroundSize: "200% 100%",
    animation: "shimmer 1.4s infinite",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  return (
    <>
      <style>{`
        @keyframes shimmer {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>

      <div ref={containerRef} style={{ display: "inline-block" }}>
        {status === "idle" || status === "loading" ? (
          <div style={placeholderStyle} />
        ) : status === "error" ? (
          <img src="/noimage.jpeg" alt={alt} style={imgStyle} />
        ) : (
          <img src={src!} alt={alt} style={imgStyle} />
        )}
      </div>
    </>
  );
}